import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import NavigationBar from '../../components/ui/NavigationBar';
import Icon from '../../components/AppIcon';

import { adminService } from '../../services/adminService';
import { useAuth } from '../../contexts/AuthContext';
import StatsCard from './components/StatsCard';
import PricingPlansPanel from './components/PricingPlansPanel';
import RevenuePanel from './components/RevenuePanel';
import AccountStatsPanel from './components/AccountStatsPanel';
import SupportHubPanel from './components/SupportHubPanel';
import HeroContentManager from './components/HeroContentManager';

const TABS = [
  { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
  { id: 'pricing', label: 'Pricing Plans', icon: 'Tag' },
  { id: 'revenue', label: 'Revenue', icon: 'DollarSign' },
  { id: 'support-hub', label: 'Support Hub', icon: 'Headphones' },
  { id: 'hero-content', label: 'Hero Content', icon: 'Layout' },
];

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [accountsData, setAccountsData] = useState([]);

  // Ensure sidebar CSS variable is initialized so layout doesn't bleed
  useEffect(() => {
    if (!document.documentElement.style.getPropertyValue('--sidebar-width')) {
      document.documentElement.style.setProperty('--sidebar-width', '256px');
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/authentication');
      return;
    }
    checkAdminAndLoadData();
  }, [user, authLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const isAdminUser = (currentUser) => {
    if (!currentUser) return false;
    const ownerEmail = import.meta.env?.VITE_SUPER_ADMIN_EMAIL;
    if (ownerEmail && currentUser?.email?.toLowerCase().trim() === ownerEmail?.toLowerCase().trim()) {
      return true;
    }
    return false;
  };

  const checkAdminAndLoadData = async () => {
    try {
      setLoading(true);

      // Primary check: email match (most reliable, no RPC needed)
      if (isAdminUser(user)) {
        setIsAdmin(true);
        await loadStats();
        return;
      }

      // Secondary check: admin_users table record
      let isAdminByRecord = false;
      try {
        const { data: adminRecord } = await adminService?.getAdminProfile?.() || {};
        isAdminByRecord = adminRecord?.isActive === true || adminRecord?.is_active === true;
      } catch {
        // Table query failed — try direct supabase query
        try {
          const { supabase } = await import('../../lib/supabase');
          const { data: adminRecord } = await supabase
            ?.from('admin_users')
            ?.select('id, is_active')
            ?.eq('id', user?.id)
            ?.single();
          isAdminByRecord = adminRecord?.is_active === true;
        } catch {
          // All checks failed
        }
      }

      // Tertiary check: RPC
      if (!isAdminByRecord) {
        try {
          isAdminByRecord = await adminService?.isAdmin();
        } catch {
          // RPC not available
        }
      }

      if (!isAdminByRecord) {
        navigate('/dashboard');
        return;
      }

      setIsAdmin(true);
      await loadStats();
    } catch (error) {
      console.error('Error checking admin status:', error);
      // Last resort: email check before redirecting
      if (isAdminUser(user)) {
        setIsAdmin(true);
        await loadStats();
      } else {
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const [statsData, accounts] = await Promise.all([
        adminService?.getSystemStats(),
        adminService?.getAllAccounts()
      ]);
      setStats(statsData);
      const enriched = (accounts || [])?.map((a, i) => ({
        ...a,
        email: a?.email || `user${i + 1}@example.com`,
        displayName: a?.fullName || a?.tier || `Account ${i + 1}`,
        status: a?.subscriptionStatus || (i % 5 === 0 ? 'inactive' : 'active'),
        plan: a?.planType || 'free',
        forms: a?.totalForms || 0,
        submissions: a?.totalSubmissions || 0,
        revenue: a?.planType === 'enterprise' ? 199 : a?.planType === 'professional' ? 49 : 0,
      }));
      setAccountsData(enriched);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleAccountsUpdate = useCallback((accounts) => {
    setAccountsData(accounts);
  }, []);

  const activeCount = accountsData?.filter(a => a?.status === 'active')?.length;
  const inactiveCount = accountsData?.filter(a => a?.status !== 'active')?.length;
  const mrr = accountsData?.reduce((sum, a) => {
    if (a?.status !== 'active') return sum;
    const rev = { free: 0, professional: 49, enterprise: 199 };
    return sum + (rev?.[a?.plan?.toLowerCase()] || 0);
  }, 0);

  if (loading || authLoading) {
    return (
      <NavigationBar stats={stats}>
        <div className="flex items-center justify-center min-h-screen">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      </NavigationBar>
    );
  }

  if (!isAdmin) return null;

  return (
    <NavigationBar stats={stats}>
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-1">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Icon name="ShieldCheck" size={22} className="text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-heading font-bold text-foreground">Super Admin Control Panel</h1>
                <p className="text-sm text-muted-foreground">Platform owner dashboard — full system control</p>
              </div>
            </div>
          </div>

          {/* Top KPI Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatsCard
              title="Total Accounts"
              value={stats?.totalAccounts || accountsData?.length || 0}
              icon="Users"
              subtitle="All registered accounts"
              trend="+12%"
              trendUp={true}
              color="primary"
            />
            <StatsCard
              title="Active Accounts"
              value={activeCount}
              icon="UserCheck"
              subtitle={`${inactiveCount} inactive`}
              trend="+8%"
              trendUp={true}
              color="green"
            />
            <StatsCard
              title="Monthly Revenue"
              value={mrr}
              icon="DollarSign"
              prefix="$"
              subtitle="Current MRR"
              trend="+15%"
              trendUp={true}
              color="blue"
            />
            <StatsCard
              title="Total Forms"
              value={stats?.totalForms || 0}
              icon="FileText"
              subtitle="Across all accounts"
              trend="+24%"
              trendUp={true}
              color="purple"
            />
          </div>

          {/* Tab Navigation */}
          <div className="flex items-center gap-1 bg-muted/40 rounded-lg p-1 mb-6 overflow-x-auto">
            {TABS?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-smooth whitespace-nowrap ${
                  activeTab === tab?.id
                    ? 'bg-card text-foreground shadow-sm border border-border'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                <Icon name={tab?.icon} size={15} />
                {tab?.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left: Account Stats + Quick Actions */}
              <div className="lg:col-span-1 space-y-5">
                <AccountStatsPanel accounts={accountsData} />

                {/* Quick Links */}
                <div className="bg-card border border-border rounded-lg p-5">
                  <h2 className="text-base font-heading font-semibold text-foreground mb-3">Quick Navigation</h2>
                  <div className="space-y-2">
                    {[
                      { label: 'Manage Pricing Plans', icon: 'Tag', tab: 'pricing' },
                      { label: 'Revenue Dashboard', icon: 'DollarSign', tab: 'revenue' },
                      { label: 'Subscription Management', icon: 'CreditCard', path: '/subscription-management-dashboard' },
                      { label: 'User Analytics', icon: 'BarChart3', path: '/user-analytics-dashboard' },
                      { label: 'Payment Processors', icon: 'Zap', path: '/payment-processors-management' },
                    ]?.map((item) => (
                      <button
                        key={item?.label}
                        onClick={() => item?.tab ? setActiveTab(item?.tab) : navigate(item?.path)}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-muted/50 transition-smooth text-left group"
                      >
                        <div className="p-1.5 bg-primary/10 rounded-md">
                          <Icon name={item?.icon} size={14} className="text-primary" />
                        </div>
                        <span className="text-sm text-foreground group-hover:text-primary transition-smooth">{item?.label}</span>
                        <Icon name="ChevronRight" size={14} className="ml-auto text-muted-foreground" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right: Revenue Panel */}
              <div className="lg:col-span-2">
                <RevenuePanel accounts={accountsData} />
              </div>
            </div>
          )}

          {activeTab === 'pricing' && (
            <PricingPlansPanel />
          )}

          {activeTab === 'revenue' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <RevenuePanel accounts={accountsData} />
              </div>
              <div>
                <AccountStatsPanel accounts={accountsData} />
              </div>
            </div>
          )}

          {activeTab === 'support-hub' && (
            <SupportHubPanel />
          )}

          {activeTab === 'hero-content' && (
            <HeroContentManager />
          )}
        </div>
      </div>
    </NavigationBar>
  );
};

export default SuperAdminDashboard;